import { useMutation } from '@apollo/client';
import type { MutationHookOptions } from '@apollo/client';
import { rescueCreatePetAdoptionTransferMutation } from './rescueQueries';
import { assignNewOwnerToChipMutation } from '@/appview/RegisterChipV2/RegisterChip.graphql';
import { mapAdopterFormToContactInfoInput } from './chipAdapters';
import type { AdopterFormData } from '../pages/Chips/OwnerFormFields';

export interface TransferPetWithChipArgs {
  petId: string;
  /** When null, only the device transfer fires. */
  chipId?: string | null;
  adopter: AdopterFormData;
}

export interface TransferPetWithChipResult {
  deviceTransferred: boolean;
  chipAssigned: boolean;
  /** Chip assignment failed after device transfer succeeded. Non-retryable. */
  chipError?: unknown;
}

/**
 * Migrate a pet's adoption transfer + chip ownership to an adopter.
 *
 * Order matters: assignNewOwnerToChip moves the pet to the new owner's
 * household when no PENDING transfer exists, which would then forbid
 * createAdoptionTransfer. Once PENDING exists the backend skips that move,
 * so device-first works end-to-end.
 *
 * Chip failure after device success is returned as `chipError`, not thrown —
 * the transfer is already PENDING and re-firing the device step would
 * duplicate it. Callers decide how to surface (toast vs modal stuck state).
 */
export function useTransferPetWithChipToAdopter(
  opts?: MutationHookOptions,
): (args: TransferPetWithChipArgs) => Promise<TransferPetWithChipResult> {
  const [createAdoptionTransfer] = useMutation(rescueCreatePetAdoptionTransferMutation, opts);
  const [assignNewOwner] = useMutation(assignNewOwnerToChipMutation, opts);

  return async function transferPetWithChip(args) {
    await createAdoptionTransfer({
      variables: {
        input: {
          petId: args.petId,
          firstName: args.adopter.firstName,
          lastName: args.adopter.lastName || undefined,
          email: args.adopter.email || undefined,
          phoneNumber: args.adopter.phone,
        },
      },
    });

    if (!args.chipId) {
      return { deviceTransferred: true, chipAssigned: false };
    }

    try {
      await assignNewOwner({
        variables: {
          input: {
            chipId: args.chipId,
            contactInfo: mapAdopterFormToContactInfoInput(args.adopter),
          },
        },
      });
      return { deviceTransferred: true, chipAssigned: true };
    } catch (chipError) {
      return { deviceTransferred: true, chipAssigned: false, chipError };
    }
  };
}
